import { memo, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SubmitHandler, useForm } from 'react-hook-form'
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import { useTwinData } from '../../../../hooks/storeHooks/useTwin'
import { scrollFieldIntoView } from '../../../../utils/scrollFieldIntoView'
import { Heading } from '../Heading'

export interface Inputs {
  firstName: string
  lastName: string
  jobTitle: string
  aboutYou?: string
}

interface ProfileTabProps {
  userData: Inputs
  isFetchingKnowledge: boolean
  openNextTab: () => void
}

const ProfileTab: React.FC<ProfileTabProps> = memo(
  ({ userData, isFetchingKnowledge, openNextTab }) => {
    const { t } = useTranslation()
    const {
      setUserData,
      setKnowledge,
      title: extractedTitle,
      aboutYou: storedAboutYou,
    } = useTwinData()

    const { register, handleSubmit, setValue, watch } = useForm<Inputs>({
      mode: 'onChange',
    })

    const watchedValues = watch()

    const isFieldValid = (value: string) => value?.trim().length > 0

    // Show the manual "About you" field when nothing could be auto-extracted, or
    // when the user already entered one manually (so it stays editable after
    // navigating forward and back).
    const showAboutYou =
      !!storedAboutYou?.trim() ||
      (!isFetchingKnowledge && !isFieldValid(extractedTitle || ''))

    const isFormValid = useMemo(() => {
      const { firstName, lastName, jobTitle, aboutYou } = watchedValues
      const baseValid = [firstName, lastName, jobTitle].every(isFieldValid)
      if (!showAboutYou) return baseValid
      return baseValid && isFieldValid(aboutYou || '')
    }, [watchedValues, showAboutYou])

    useEffect(() => {
      if (userData) {
        setValue('firstName', userData.firstName || '')
        setValue('lastName', userData.lastName || '')
        setValue('jobTitle', userData.jobTitle || '')
      }
      // Prefill the previously-entered "About you" so it can be edited on return.
      setValue('aboutYou', storedAboutYou || '')
    }, [userData, isFetchingKnowledge, storedAboutYou, setValue])

    const onSubmit: SubmitHandler<Inputs> = data => {
      if (showAboutYou && data.aboutYou) {
        setKnowledge({ knowledge: data.aboutYou, jobTitle: data.jobTitle })
      }
      setUserData(data)
      openNextTab()
    }

    const inputClassName =
      'px-4 tc-mv-blur h-10 text-white rounded-full w-full mb-2'
    const fieldValidation = {
      required: true,
      validate: (value: string) => isFieldValid(value),
    }

    return (
      <div className="w-full md:w-[600px] text-white flex flex-col items-center justify-center mb-auto sm:mb-0">
        <Heading
          title={t('twin.profileTitle')}
          subtitle={t('twin.profileSubtitle')}
        />
        <form
          className="flex flex-col w-full"
          onSubmit={handleSubmit(onSubmit)}
          onFocus={scrollFieldIntoView}
        >
          <div className="">
            <label htmlFor="firstName" className="block mb-2 text-sm">
              {t('twin.firstNameLabel')}
            </label>
            <input
              id="firstName"
              type="text"
              className={inputClassName}
              placeholder={t('twin.firstNamePlaceholder')}
              {...register('firstName', fieldValidation)}
            />
            <label htmlFor="lastName" className="block mb-2 text-sm">
              {t('twin.lastNameLabel')}
            </label>
            <input
              id="lastName"
              type="text"
              className={inputClassName}
              placeholder={t('twin.lastNamePlaceholder')}
              {...register('lastName', fieldValidation)}
            />
            <div className="relative">
              <label htmlFor="jobTitle" className="block mb-2 text-sm">
                {t('twin.jobTitleLabel')}
              </label>
              {isFetchingKnowledge && (
                <div className="absolute left-4  bottom-10 z-10">
                  <AnimatedSpinner />
                </div>
              )}
              <input
                id="jobTitle"
                type="text"
                className={`${isFetchingKnowledge ? 'px-12' : 'px-4'} tc-mv-blur h-10 text-white rounded-full w-full mb-8`}
                placeholder={
                  isFetchingKnowledge
                    ? t('twin.extractingJobTitle')
                    : t('twin.jobTitlePlaceholder')
                }
                disabled={isFetchingKnowledge}
                {...register('jobTitle', fieldValidation)}
              />
            </div>
            {showAboutYou && (
              <div>
                <label htmlFor="aboutYou" className="block mb-2 text-sm">
                  {t('twin.aboutYouLabel')}
                </label>
                <textarea
                  id="aboutYou"
                  rows={4}
                  className="px-4 py-3 tc-mv-blur text-white rounded-2xl w-full mb-8 resize-none"
                  placeholder={t('twin.aboutYouPlaceholder')}
                  {...register('aboutYou', {
                    required: showAboutYou,
                    validate: value => !showAboutYou || isFieldValid(value || ''),
                  })}
                />
              </div>
            )}
          </div>
          <div className="mx-auto">
            <button
              data-testid="profile-continue-btn"
              disabled={!isFormValid}
              className="btn btn-primary"
            >
              {t('twin.continue')}
              <i className="fa-solid fa-arrow-right ml-2" />
            </button>
          </div>
        </form>
      </div>
    )
  },
)

export { ProfileTab }
